'use client'

/* Follow Members widget (DARK behind userFollows — the parent page only renders this when the flag is
   on). Fetches the viewer's follow-status vs this user and shows the right control:
     none → Follow · pending_outgoing → Requested (tap to cancel) · pending_incoming → Approve/Reject ·
     accepted → Following (tap to unfollow) · blocked_by_me → Blocked (tap to unblock).
   Plus a Block action. All writes hit the block-aware endpoints; the raw block state of the OTHER user
   is never exposed (the server maps "they blocked me" to none). Nothing renders on your own profile. */

import { useCallback, useEffect, useState } from 'react'

type Status = 'none' | 'pending_outgoing' | 'pending_incoming' | 'accepted' | 'blocked_by_me'

export default function FollowWidget({ userId }: { userId: string }) {
  const [status, setStatus] = useState<Status | null>(null)
  const [isSelf, setIsSelf] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [unavailable, setUnavailable] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/users/${userId}/follow-status`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      if (res.status === 401) { setUnavailable(true); setLoading(false); return } // not signed in
      if (!res.ok) { setUnavailable(true); setLoading(false); return }
      const data = await res.json()
      setStatus(data.status as Status)
      setIsSelf(!!data.isSelf)
    } catch {
      setUnavailable(true)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => { void refresh() }, [refresh])

  const call = useCallback(async (path: string, method: string, body?: unknown) => {
    if (busy) return
    setBusy(true)
    try {
      await fetch(path, {
        method,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        cache: 'no-store',
      })
      await refresh()
    } catch { /* surface nothing — refresh shows the real state */ } finally {
      setBusy(false)
    }
  }, [busy, refresh])

  if (loading) return <div style={{ height: 38 }} />
  if (unavailable || isSelf || status == null) return null

  const base: React.CSSProperties = {
    padding: '9px 18px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer',
    border: '1px solid transparent', opacity: busy ? 0.6 : 1,
  }
  const primary: React.CSSProperties = { ...base, background: '#04231A', color: '#fff' }
  const neutral: React.CSSProperties = { ...base, background: '#f3f4f6', color: '#111827', borderColor: '#e5e7eb' }
  const danger: React.CSSProperties = { ...base, background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
        {status === 'none' && (
          <button type="button" disabled={busy} style={primary} onClick={() => call(`/api/users/${userId}/follow`, 'POST')}>Follow</button>
        )}
        {status === 'pending_outgoing' && (
          <button type="button" disabled={busy} style={neutral} onClick={() => call(`/api/users/${userId}/follow`, 'DELETE')}>Requested</button>
        )}
        {status === 'pending_incoming' && (
          <>
            <button type="button" disabled={busy} style={primary} onClick={() => call(`/api/users/${userId}/follow/respond`, 'POST', { action: 'accept' })}>Approve</button>
            <button type="button" disabled={busy} style={neutral} onClick={() => call(`/api/users/${userId}/follow/respond`, 'POST', { action: 'reject' })}>Reject</button>
          </>
        )}
        {status === 'accepted' && (
          <button type="button" disabled={busy} style={neutral} onClick={() => call(`/api/users/${userId}/follow`, 'DELETE')}>Following ✓</button>
        )}
        {status === 'blocked_by_me' && (
          <button type="button" disabled={busy} style={danger} onClick={() => call(`/api/users/${userId}/block`, 'DELETE')}>Blocked · Unblock</button>
        )}
      </div>
      {status !== 'blocked_by_me' && (
        <button
          type="button"
          disabled={busy}
          onClick={() => call(`/api/users/${userId}/block`, 'POST')}
          style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, fontWeight: 600, cursor: busy ? 'default' : 'pointer', textDecoration: 'underline' }}
        >
          Block
        </button>
      )}
    </div>
  )
}
